import { useEffect, useMemo, useState } from 'react';
import {
  ColumnDef,
  OnChangeFn,
  PaginationState,
  getCoreRowModel,
  useReactTable,
  RowSelectionState,
  RowModel,
  Row,
} from '@tanstack/react-table';
import { TablePaginationType } from '@/shared/types/common';
import { TableBody, TableFooter, TableHeader } from '../../components';
import styles from './AppTable.module.scss';

export interface NavigationConfig<TData> {
  columnId: string;
  getUrl: (row: TData) => string;
}

export interface AppTableProps<TData> {
  data: TData[];
  meta?: TablePaginationType;
  columns: ColumnDef<TData>[];
  pagination?: PaginationState;
  setPagination?: OnChangeFn<PaginationState>;
  getSelectedItems: (rows: TData[]) => void;
  navigationConfig?: NavigationConfig<TData>[];
  onRowClick?: (row: TData) => void;
  getRowClassName?: (row: TData) => string | undefined;
  customTableBody?: (rows: RowModel<TData>['rows']) => React.ReactNode;
  tableMeta?: Record<string, any>;
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
  /** Ключ строки для выбора и рендера; по умолчанию `row.id` */
  getRowId?: (row: TData) => string;
}

const fallbackData: never[] = [];
const LONG_TEXT_MIN_WIDTH = 400;
const LONG_TEXT_THRESHOLD = 28;
const LONG_TEXT_SAMPLE_LIMIT = 50;

export const AppTable = <T,>(props: AppTableProps<T>) => {
  const {
    data,
    meta,
    columns,
    pagination,
    setPagination,
    getSelectedItems,
    navigationConfig,
    onRowClick,
    getRowClassName,
    customTableBody,
    tableMeta,
    enableRowSelection = true,
    getRowId = (row) => String((row as { id?: unknown }).id ?? ''),
  } = props;
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const table = useReactTable({
    columns,
    data: data || fallbackData,
    manualPagination: true,
    state: { pagination, rowSelection },
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getRowId,
    enableRowSelection,
    meta: tableMeta,
  });

  useEffect(() => {
    setRowSelection({});
  }, [data]);

  useEffect(() => {
    const selectedRows = table.getSelectedRowModel().rows.map((row) => row.original);
    getSelectedItems(selectedRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection]);

  const columnMinWidths = useMemo(() => {
    const widths: Record<string, number> = {};
    const rows = table.getRowModel().rows;

    table.getAllLeafColumns().forEach((column) => {
      const headerDef = column.columnDef.header;
      const headerText =
        typeof headerDef === 'string'
          ? headerDef
          : typeof column.id === 'string'
            ? column.id
            : '';

      const normalizedHeader = headerText.trim().toLowerCase();
      if (normalizedHeader === 'course identifier') {
        widths[column.id] = LONG_TEXT_MIN_WIDTH;
        return;
      }

      let hasLongText = false;
      for (let i = 0; i < rows.length && i < LONG_TEXT_SAMPLE_LIMIT; i += 1) {
        const value = rows[i].getValue(column.id);
        if (typeof value === 'string' && value.trim().length >= LONG_TEXT_THRESHOLD) {
          hasLongText = true;
          break;
        }
      }

      if (hasLongText) {
        widths[column.id] = LONG_TEXT_MIN_WIDTH;
      }
    });

    return widths;
  }, [table, data]);

  const rows = table.getRowModel().rows;
  return (
    <div className={styles.root}>
      {rows?.length > 0 ? (
        <>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <TableHeader
                getHeaderGroups={table.getHeaderGroups}
                columnMinWidths={columnMinWidths}
              />
              {customTableBody ? (
                customTableBody(rows)
              ) : (
                <TableBody
                  rows={rows}
                  navigationConfig={navigationConfig}
                  onRowClick={onRowClick}
                  getRowClassName={getRowClassName}
                  columnMinWidths={columnMinWidths}
                />
              )}
            </table>
          </div>
          {meta && pagination && setPagination && (
            <TableFooter
              total={meta.pageCount}
              pagination={pagination}
              setPagination={setPagination}
            />
          )}
        </>
      ) : (
        <div className="py-30 px-10 w-full text-center h-full">No data available</div>
      )}
    </div>
  );
};
